import * as React from 'react';
import { useMemo, useState } from 'react';

import { translate } from '~l10n/client';

import {
  IconButton,
  UIButton,
  UIButtonGroup,
} from '~shared/components/buttons';
import { Dialog, DialogProps, DialogTitle } from '~shared/components/Dialog';
import { IconArrow } from '~shared/components/icons/large';
import { IconChevron } from '~shared/components/icons/standard';
import { ReportRecipientsScreen } from '~teacher/enums';
import { Student } from '~teacher/models/reports';

import { ExportFeedbackEmailStudentComplete } from '../ExportReports/ExportFeedback/ExportFeedbackEmailStudent';
import { Error } from '../ExportReports/ExportFeedback/ExportFeedbackIcons';
import EmailStudentsDialogFooter from './EmailStudentsDialogFooter';
import { EmailStudentsFeedback } from './EmailStudentsFeedback';
import EmailStudentsOptions from './EmailStudentsOptions';
import EmailStudentsRecipientsList from './EmailStudentsRecipientsList';
import {
  getNumStudentEmailsAndParticipated,
  getNumStudentsSelected,
  getUIButtonText,
} from './emailStudentsUtils';
import { SendEmailOptions } from './ShareResults';

export interface Props extends Pick<DialogProps, 'open' | 'onClose'> {
  students: Student[];
  onEmailIndividuals: (options: SendEmailOptions) => void;
  onEmailAllStudents: (options: SendEmailOptions) => void;
  isRequestInProgress: boolean;
  currentModalView: string;
  setCurrentModalView: (screen: ReportRecipientsScreen) => void;
  onSelectAll: () => void;
  onSelectNone: () => void;
}

export function EmailStudentsDialog({
  open,
  onClose,
  students,
  onEmailIndividuals,
  onEmailAllStudents,
  isRequestInProgress,
  currentModalView,
  setCurrentModalView,
  onSelectAll,
  onSelectNone,
}: Props) {
  const [isIncludeExplanationsToggled, setIsIncludeExplanationsToggled] =
    useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogDescription, setDialogDescription] = useState('');
  const [showHeader, setShowHeader] = useState(false);
  const [isIncludeCorrectAnswersToggled, setIsIncludeCorrectAnswersToggled] =
    useState(false);

  React.useEffect(() => {
    switch (currentModalView) {
      case ReportRecipientsScreen.REPORT_OPTIONS_VIEW:
        setShowHeader(true);
        setDialogTitle(translate('Email Results to Students'));
        !isRequestInProgress &&
          setDialogDescription(
            translate(
              'Send a personalized results PDF to each student who took part in the activity.'
            )
          );
        break;
      case ReportRecipientsScreen.EMAIL_STUDENTS_VIEW:
        setShowHeader(true);
        setDialogTitle(translate('Select Recipients'));
        !isRequestInProgress &&
          setDialogDescription(
            translate(
              'Select one or more students to receive personalized results PDFs.'
            )
          );
        break;
      case ReportRecipientsScreen.EMAIL_SUCCESS_VIEW:
        setShowHeader(false);
        setDialogTitle(translate('Sending Results'));
        break;
      case ReportRecipientsScreen.ROSTER_FETCH_ERROR:
        setShowHeader(false);
        setDialogTitle(translate('Student Roster Error'));
        break;
      case ReportRecipientsScreen.ERROR_FEEDBACK_VIEW:
        setShowHeader(false);
        setDialogTitle(translate('Something Went Wrong'));
        break;
      default:
        setDialogDescription(undefined);
    }
  }, [currentModalView, isRequestInProgress]);

  const handleIncludeCorrectAnswersToggle = () => {
    setIsIncludeCorrectAnswersToggled(!isIncludeCorrectAnswersToggled);
  };

  const handleIsIncludeExplanationToggle = () => {
    setIsIncludeExplanationsToggled(!isIncludeExplanationsToggled);
  };

  const numStudentsSelected = useMemo(() => {
    return getNumStudentsSelected(students);
  }, [students]);
  const numStudentEmailsAndParticipated = useMemo(
    () => getNumStudentEmailsAndParticipated(students),
    [students]
  );
  const shouldDisableSelectAll =
    numStudentsSelected === numStudentEmailsAndParticipated ||
    isRequestInProgress;
  const shouldDisableSelectNone =
    numStudentsSelected === 0 || isRequestInProgress;
  const hasEmails = students.some((student: Student) => {
    return !!student.email;
  });
  const handleBackClicked = () => {
    setCurrentModalView(ReportRecipientsScreen.REPORT_OPTIONS_VIEW);
  };
  const handleEmailIndividualsClicked = () => {
    setCurrentModalView(ReportRecipientsScreen.EMAIL_STUDENTS_VIEW);
  };

  const showSuccessFeedbackView =
    ReportRecipientsScreen.EMAIL_SUCCESS_VIEW === currentModalView;
  const uiButtonText = useMemo(() => {
    return getUIButtonText(numStudentsSelected, students);
  }, [numStudentsSelected, students]);
  const shouldDisableReportOptionsFooter =
    isRequestInProgress || numStudentEmailsAndParticipated === 0;

  return (
    <Dialog
      id="email-students"
      size="medium"
      open={open}
      title={dialogTitle}
      description={
        dialogDescription && (
          <p className="m-0 pt-4 pr-6 text-base text-blue-600 leading-8">
            {dialogDescription}
          </p>
        )
      }
      __customClassEscapeHatch="h-full sm:h-screen-h-3/5"
      onClose={onClose}
      closeLabel={translate('Close')}
    >
      {({ headingRef, title, description, descriptionId }) => (
        <div className="flex flex-col h-full w-full">
          {showHeader ? (
            <header className="m-0">
              <div className="flex">
                {currentModalView ===
                ReportRecipientsScreen.EMAIL_STUDENTS_VIEW ? (
                  <div className="flex mr-4 -ml-2">
                    <IconButton
                      aria-label={translate('Back')}
                      onClick={handleBackClicked}
                      appearance="ghost"
                    >
                      <IconArrow direction="left" />
                    </IconButton>
                  </div>
                ) : null}
                <h3 className="flex m-0 py-2">
                  <DialogTitle weight="bold" ref={headingRef}>
                    {title}
                  </DialogTitle>
                </h3>
              </div>
              {description ? <div id={descriptionId}>{description}</div> : null}
            </header>
          ) : null}

          {currentModalView === ReportRecipientsScreen.ERROR_FEEDBACK_VIEW && (
            <EmailStudentsFeedback
              icon={<Error />}
              title={title}
              onBack={handleBackClicked}
              message={translate('Sorry, something unexpected happened.')}
              ref={headingRef}
            >
              <UIButton onClick={onClose}>{translate('Close')}</UIButton>
            </EmailStudentsFeedback>
          )}
          {currentModalView === ReportRecipientsScreen.ROSTER_FETCH_ERROR && (
            <EmailStudentsFeedback
              icon={<Error />}
              title={title}
              message={translate('Sorry, something unexpected happened.')}
            >
              <UIButton onClick={onClose}>{translate('Close')}</UIButton>
            </EmailStudentsFeedback>
          )}
          {showSuccessFeedbackView && (
            <ExportFeedbackEmailStudentComplete onBack={handleBackClicked} />
          )}

          {currentModalView === ReportRecipientsScreen.REPORT_OPTIONS_VIEW && (
            <EmailStudentsOptions
              hideToggleOptions={students.length === 0 || !hasEmails}
              isRequestInProgress={isRequestInProgress}
              isIncludeCorrectAnswersToggled={isIncludeCorrectAnswersToggled}
              isIncludeExplanationsToggled={isIncludeExplanationsToggled}
              handleIncludeCorrectAnswersToggle={
                handleIncludeCorrectAnswersToggle
              }
              handleIsIncludeExplanationToggle={
                handleIsIncludeExplanationToggle
              }
            >
              <EmailStudentsDialogFooter>
                <UIButton
                  isDisabled={shouldDisableReportOptionsFooter}
                  className="w-full -mt-4 sm:w-auto sm:mt-0 self-center flex justify-center"
                  appearance="text"
                  onClick={handleEmailIndividualsClicked}
                >
                  <div className="flex items-center">
                    {translate('Select Recipients')}
                    <IconChevron direction="right" />
                  </div>
                </UIButton>
                <UIButton
                  isDisabled={shouldDisableReportOptionsFooter}
                  onClick={() => {
                    onEmailAllStudents({
                      includeCorrectAnswers: isIncludeCorrectAnswersToggled,
                      includeExplanations: isIncludeExplanationsToggled,
                    });
                  }}
                  type="submit"
                  className="self-end w-full mt-4 sm:w-auto sm:mt-0"
                >
                  {translate('Email All Students')}
                </UIButton>
              </EmailStudentsDialogFooter>
            </EmailStudentsOptions>
          )}
          {currentModalView === ReportRecipientsScreen.EMAIL_STUDENTS_VIEW && (
            <EmailStudentsRecipientsList
              isRequestInProgress={isRequestInProgress}
              students={students}
              handleBackClicked={handleBackClicked}
            >
              <EmailStudentsDialogFooter>
                <UIButtonGroup className="w-full -mt-4 sm:w-auto sm:mt-0">
                  <UIButton
                    key="select all"
                    isDisabled={shouldDisableSelectAll}
                    onClick={onSelectAll}
                  >
                    {translate('Select All')}
                  </UIButton>
                  <UIButton
                    key="select none"
                    isDisabled={shouldDisableSelectNone}
                    onClick={onSelectNone}
                  >
                    {translate('Select None')}
                  </UIButton>
                </UIButtonGroup>
                <UIButton
                  isDisabled={shouldDisableSelectNone}
                  onClick={() => {
                    onEmailIndividuals({
                      includeCorrectAnswers: isIncludeCorrectAnswersToggled,
                      includeExplanations: isIncludeExplanationsToggled,
                    });
                  }}
                  type="submit"
                  className="self-end w-full mt-4 sm:w-auto sm:mt-0"
                >
                  {uiButtonText}
                </UIButton>
              </EmailStudentsDialogFooter>
            </EmailStudentsRecipientsList>
          )}
        </div>
      )}
    </Dialog>
  );
}
